import React, { useState, useMemo } from "react";
import {
  TASKS,
  SCHEDULABLE_IDS,
  Q3_IDS,
  Q4_IDS,
  QUADRANT_META,
  ENV_TOGGLES,
  BLOCK_COUNT,
} from "./data.js";

const taskById = Object.fromEntries(TASKS.map((t) => [t.id, t]));
const ACCENT_TEXT = { danger: "text-danger", lime: "text-lime", gold: "text-gold", mist: "text-mist" };

export default function FormationStage({ onComplete }) {
  const [blocks, setBlocks] = useState(Array(BLOCK_COUNT).fill(null));
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [q3Resolutions, setQ3Resolutions] = useState({});
  const [q4Resolutions, setQ4Resolutions] = useState({});
  const [envToggles, setEnvToggles] = useState({});

  const placedIds = blocks.filter(Boolean);
  const pendingTasks = SCHEDULABLE_IDS.filter((id) => !placedIds.includes(id));

  const placeInBlock = (blockIndex) => {
    if (blocks[blockIndex] || !selectedTaskId) return;
    const next = [...blocks];
    next[blockIndex] = selectedTaskId;
    setBlocks(next);
    setSelectedTaskId(null);
  };

  const clearBlock = (blockIndex) => {
    const next = [...blocks];
    next[blockIndex] = null;
    setBlocks(next);
  };

  const emptyBlockCount = blocks.filter((b) => !b).length;
  const allTasksPlaced = pendingTasks.length === 0;
  const block1Task = blocks[0] ? taskById[blocks[0]] : null;
  const frogValid = block1Task?.quadrant === "Q1";

  const q3Done = Q3_IDS.every((id) => q3Resolutions[id]);
  const q4Done = Q4_IDS.every((id) => q4Resolutions[id]);

  const canLockIn = allTasksPlaced && emptyBlockCount === 1 && frogValid && q3Done && q4Done;

  const shieldsCount = useMemo(
    () => ENV_TOGGLES.filter((t) => envToggles[t.id]).length,
    [envToggles]
  );

  const handleLockIn = () => {
    if (!canLockIn) return;
    onComplete({
      blocks,
      bufferIndex: blocks.findIndex((b) => !b),
      q3Resolutions,
      q4Resolutions,
      shieldsCount,
      envToggles,
    });
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between font-display text-[9px] text-mist/60">
        <span>BATTLE FORMATION</span>
        <span>{BLOCK_COUNT} BLOCKS · 90 MIN EACH</span>
      </div>

      <p className="mb-5 text-xs text-mist/60">
        Tap a task, then tap a block to place it. Block 1 must hold your hardest, highest-stakes
        task — that's <span className="text-cyan">Eat That Frog</span>. Leave exactly one block
        empty as your <span className="text-lime">buffer</span> for the unexpected.
      </p>

      {/* Pending task pool */}
      {pendingTasks.length > 0 && (
        <div className="mb-5">
          <p className="label-pixel mb-2 text-mist/60">Unscheduled — tap to select</p>
          <div className="flex flex-wrap gap-2">
            {pendingTasks.map((id) => {
              const task = taskById[id];
              const meta = QUADRANT_META[task.quadrant];
              const selected = selectedTaskId === id;
              return (
                <button
                  key={id}
                  onClick={() => setSelectedTaskId(selected ? null : id)}
                  className={`border-2 px-3 py-2 text-left text-[11px] transition-colors ${
                    selected ? "border-cyan bg-cyan/10 text-cyan" : "border-mist/30 text-mist/80 hover:border-cyan"
                  }`}
                >
                  <span className={`mr-2 font-display text-[7px] ${ACCENT_TEXT[meta.accent]}`}>{meta.label}</span>
                  {task.description.length > 38 ? task.description.slice(0, 38) + "…" : task.description}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Block grid */}
      <div className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-3">
        {blocks.map((taskId, i) => {
          const task = taskId ? taskById[taskId] : null;
          const isFirst = i === 0;
          return (
            <button
              key={i}
              onClick={() => (task ? clearBlock(i) : placeInBlock(i))}
              className={`flex min-h-[84px] flex-col items-start justify-between border-2 p-2 text-left transition-colors ${
                isFirst ? "border-danger" : "border-mist/30"
              } ${task ? "bg-panel2" : selectedTaskId ? "bg-ink hover:border-cyan" : "bg-ink"}`}
            >
              <span className="font-display text-[7px] uppercase text-mist/50">
                {isFirst ? "🔥 Block 1 · Frog" : `Block ${i + 1}`}
              </span>
              {task ? (
                <span className="text-[10px] leading-snug text-mist">
                  {task.description.length > 46 ? task.description.slice(0, 46) + "…" : task.description}
                </span>
              ) : (
                <span className="text-[10px] italic text-mist/30">Empty — tap to fill</span>
              )}
            </button>
          );
        })}
      </div>

      {block1Task && !frogValid && (
        <p className="mb-4 text-[11px] text-danger">
          That's not your frog. Block 1 needs a Do-First (Q1) task — clear it and place your
          hardest, highest-stakes item there instead.
        </p>
      )}
      {allTasksPlaced && emptyBlockCount !== 1 && (
        <p className="mb-4 text-[11px] text-danger">
          You need exactly one empty block reserved as buffer.
        </p>
      )}

      {/* Q3 — Delegate or Decline */}
      <section className="panel mb-4 p-4">
        <p className="mb-2 font-display text-[10px] text-gold">DELEGATE OR DECLINE</p>
        <p className="mb-3 text-[11px] text-mist/50">
          Urgent, not important. These don't get a block — they get a decision.
        </p>
        {Q3_IDS.map((id) => {
          const task = taskById[id];
          const resolved = q3Resolutions[id];
          return (
            <div key={id} className="mb-3 last:mb-0">
              <p className="mb-2 text-[11px] text-mist/80">{task.description}</p>
              <div className="grid gap-2">
                {task.resolutionOptions.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setQ3Resolutions((prev) => ({ ...prev, [id]: opt.id }))}
                    className={`border-2 px-3 py-2 text-left text-[10px] transition-colors ${
                      resolved === opt.id
                        ? opt.tag === "good"
                          ? "border-lime bg-lime/10 text-lime"
                          : "border-danger bg-danger/10 text-danger"
                        : "border-mist/20 text-mist/70 hover:border-gold"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {resolved && (
                <p className="mt-1 text-[10px] text-mist/50">
                  {task.resolutionOptions.find((o) => o.id === resolved)?.note}
                </p>
              )}
            </div>
          );
        })}
      </section>

      {/* Q4 — Cut */}
      <section className="panel mb-4 p-4">
        <p className="mb-2 font-display text-[10px] text-mist">CUT</p>
        <p className="mb-3 text-[11px] text-mist/50">Not urgent, not important. Time-wasters.</p>
        {Q4_IDS.map((id) => {
          const task = taskById[id];
          const resolved = q4Resolutions[id];
          return (
            <div key={id}>
              <p className="mb-2 text-[11px] text-mist/80">{task.description}</p>
              <div className="grid gap-2">
                {task.resolutionOptions.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setQ4Resolutions((prev) => ({ ...prev, [id]: opt.id }))}
                    className={`border-2 px-3 py-2 text-left text-[10px] transition-colors ${
                      resolved === opt.id
                        ? opt.tag === "good"
                          ? "border-lime bg-lime/10 text-lime"
                          : "border-danger bg-danger/10 text-danger"
                        : "border-mist/20 text-mist/70 hover:border-mist"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {resolved && (
                <p className="mt-1 text-[10px] text-mist/50">
                  {task.resolutionOptions.find((o) => o.id === resolved)?.note}
                </p>
              )}
            </div>
          );
        })}
      </section>

      {/* Environment setup */}
      <section className="panel mb-6 p-4">
        <p className="mb-1 font-display text-[10px] text-cyan">FOCUS ENVIRONMENT</p>
        <p className="mb-3 text-[11px] text-mist/50">
          Optional, but each one earns a Focus Shield you can use to deflect an interruption later.
        </p>
        <div className="space-y-2">
          {ENV_TOGGLES.map((t) => {
            const on = !!envToggles[t.id];
            return (
              <button
                key={t.id}
                onClick={() => setEnvToggles((prev) => ({ ...prev, [t.id]: !prev[t.id] }))}
                className={`flex w-full items-center justify-between border-2 px-3 py-2 text-left transition-colors ${
                  on ? "border-cyan bg-cyan/10" : "border-mist/20"
                }`}
              >
                <span>
                  <span className={`block font-display text-[9px] ${on ? "text-cyan" : "text-mist/70"}`}>{t.label}</span>
                  <span className="text-[10px] text-mist/40">{t.description}</span>
                </span>
                <span className={`font-display text-[9px] ${on ? "text-cyan" : "text-mist/30"}`}>{on ? "ON" : "OFF"}</span>
              </button>
            );
          })}
        </div>
        {shieldsCount > 0 && (
          <p className="mt-3 text-[11px] text-cyan">🛡️ {shieldsCount} Focus Shield{shieldsCount === 1 ? "" : "s"} ready for the raid.</p>
        )}
      </section>

      <button onClick={handleLockIn} disabled={!canLockIn} className="btn-magenta w-full disabled:opacity-30">
        {canLockIn ? "LOCK IN TODAY'S PLAN ▶" : "COMPLETE THE FORMATION TO LOCK IN"}
      </button>
    </div>
  );
}
